import { useState, useMemo } from 'react';
import { Copy, Sparkles } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Field } from '@/components/ui/Input';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

const SAMPLE = `The Utility OS is a browser-native productivity surface. Instead of jumping between dozens of separate websites for PDF editing, image compression, CSV cleanup, QR generation and timezone conversion, employees can complete the same tasks in one cohesive workspace. Files are processed locally in the browser, which keeps sensitive documents private and removes the need to upload to third-party servers. A universal command bar lets users run any tool by name or by intent, while the agent layer routes natural-language requests to the right utility. Workflows can be saved and chained, turning ad-hoc work into reusable automations. The end result is fewer tabs, less context-switching, and dramatically faster handling of the long tail of daily operational tasks.`;

interface Sentence {
  text: string;
  score: number;
  index: number;
}

const STOP = new Set([
  'the','a','an','and','or','but','if','to','of','in','on','for','with','as','by','at','from','this','that','it','is','are','was','were','be','been','being','have','has','had','do','does','did','will','would','can','could','should','may','might','must','shall','about','into','than','then','so','such','what','which','when','where','who','how','its',
]);

function summarize(text: string, sentenceCount: number): string {
  const sentences = text
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .filter((s) => s.trim().length > 0);
  if (sentences.length <= sentenceCount) return text;

  // Frequency-based scoring
  const words = text.toLowerCase().match(/[a-z]{3,}/g) ?? [];
  const freq: Record<string, number> = {};
  for (const w of words) {
    if (STOP.has(w)) continue;
    freq[w] = (freq[w] ?? 0) + 1;
  }

  const scored: Sentence[] = sentences.map((s, i) => {
    const tokens = s.toLowerCase().match(/[a-z]{3,}/g) ?? [];
    let score = 0;
    for (const t of tokens) if (!STOP.has(t)) score += freq[t] ?? 0;
    score = score / Math.sqrt(Math.max(1, tokens.length));
    // Bias intro/outro
    if (i === 0) score *= 1.1;
    return { text: s, score, index: i };
  });

  const top = [...scored].sort((a, b) => b.score - a.score).slice(0, sentenceCount);
  return top.sort((a, b) => a.index - b.index).map((s) => s.text).join(' ');
}

export function Summarizer() {
  const [text, setText] = useState(SAMPLE);
  const [n, setN] = useState(3);

  const summary = useMemo(() => (text ? summarize(text, n) : ''), [text, n]);
  const inputWords = text.trim().split(/\s+/).filter(Boolean).length;
  const outputWords = summary.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div>
      <ToolHeader
        toolId="summarizer"
        title="Extractive summarizer"
        description="Distill text to its most representative sentences using frequency analysis. Runs locally."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          <Field label="Sentences" hint={`${n}`}>
            <Slider value={n} onChange={setN} min={1} max={8} showValue={false} />
          </Field>
          <div className="border-t border-border pt-3 text-xs space-y-1.5 font-mono">
            <Row label="Input" value={`${inputWords} words`} />
            <Row label="Summary" value={`${outputWords} words`} />
            <Row
              label="Compression"
              value={`${Math.round((1 - outputWords / Math.max(1, inputWords)) * 100)}%`}
              highlight
            />
          </div>
          <Button
            variant="primary"
            className="w-full"
            onClick={async () => {
              const ok = await copyToClipboard(summary);
              if (ok) toast.success('Summary copied');
            }}
          >
            <Copy size={11} />
            Copy summary
          </Button>
        </Card>
        <div className="lg:col-span-2 lg:order-1 space-y-3">
          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Source
            </div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="!border-0 !rounded-t-none min-h-[260px] !bg-transparent focus-visible:!ring-0"
            />
          </Card>
          <Card>
            <div className="text-xs font-medium text-fg-muted mb-2 flex items-center gap-1.5">
              <Sparkles size={11} className="text-orange-500 dark:text-amber-400" />
              Summary
            </div>
            <div className="text-sm leading-relaxed">{summary}</div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-fg-subtle">{label}</span>
      <span className={highlight ? 'text-accent font-semibold' : ''}>{value}</span>
    </div>
  );
}

export default Summarizer;
